import { Button, Input, message, Radio } from "antd";

import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { stringHasURL } from "shared/helpers/url-helper";
import { fetchSpotifyArtists } from "shared/service/StrmService";
import { EditOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpotify } from "@fortawesome/free-brands-svg-icons";
import SpofityMockup from "shared/assets/images/spotify-mockup.png";
import SpofityMockupLink from "shared/assets/images/spotify-mockup-link.png";

const defaultArtistImage =
  "https://placehold.co/100x100/292929/f2f2f2?text=Artist";

enum Step {
  Search = "SEARCH",
  Select = "SELECT",
  Help = "HELP",
}

type Result = {
  name?: string;
  id?: string;
  avatarUrl?: string;
};

type SpotifySearchProps = {
  value?: Result;
  onChange?: (option?: Result) => void;
  onClickNext?: () => void;
  countryCode?: string;
};

const SpotifySearch: React.FC<SpotifySearchProps> = (props) => {
  const { value, countryCode, onChange = (_: any) => {}, onClickNext } = props;
  const { t } = useTranslation();

  const [fieldValue, setFieldValue] = useState<string>("");
  const [searchField, setSearchField] = useState<string>("");
  const [results, setResults] = useState<Result[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [step, setStep] = useState<Step>(Step.Search);
  // const [selectedArtistId, setSelectedArtistId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const prevSearchFieldRef = useRef<string>("");

  const getSpotifyArtists = useCallback(async () => {
    if (
      searchField &&
      countryCode &&
      searchField !== prevSearchFieldRef.current
    ) {
      prevSearchFieldRef.current = searchField;
      setLoading(true);
      try {
        const term = stringHasURL(searchField)
          ? encodeURIComponent(searchField)
          : searchField;
        const artists: any = await fetchSpotifyArtists(
          encodeURIComponent(term),
          countryCode,
          offset
        );
        const artistsOption = artists.map(({ name, id, ...artist }: any) => ({
          name,
          id,
          avatarUrl: artist?.images[0]?.url,
        }));
        if (artistsOption.length === 0) {
          message.error(t("quiz.pages.Questionario.spotifyNotFound"));
        } else {
          if (artistsOption.length === 1) {
            onChange(artistsOption[0]);
            setStep(Step.Search);
          } else {
            setResults(artistsOption);
            setStep(Step.Select);
          }
        }
      } catch {
        onChange({
          id: "no-data",
          name: t("quiz.pages.Questionario.spotifyNotFound"),
        });
      } finally {
        setLoading(false);
      }
    }
  }, [searchField, offset, t, onChange, countryCode]);

  const onSearch = () => {
    if (!fieldValue.trim()) return;
    if (searchField === fieldValue) {
      setStep(Step.Select);
    } else {
      setSearchField(fieldValue);
      setOffset(0);
    }
  };

  const onSelectArtist = (option?: Result) => {
    onChange(option);
  };

  const onKeyPress = (event: any) => {
    if (event.which === 13) {
      onSearch();
    }
  };

  // const onClickNoValue = (checked: boolean) => {
  //   if (checked) {
  //     const noDataValue = { id: "no-data" };
  //     onChange(noDataValue);
  //     // setSelectedOption(noDataValue);
  //     // onSelectCallback(false);
  //   } else {
  //     onChange();
  //     // setSelectedOption(undefined);
  //   }
  // };

  const onClickContinue = () => {
    if (onClickNext) onClickNext();
  };

  const onClickEdit = () => {
    setStep(Step.Search);
    // onSelectArtist();
  };

  useEffect(() => {
    getSpotifyArtists();
  }, [getSpotifyArtists]);

  useEffect(() => {
    if (step === Step.Select) {
      dataLayerViewedEvent({
        ...tagManagerEvent.FreeAssessment.SPOTIFY_SELECTION_NEW_ASSESSMENT,
        version: "V3",
      });
    }

    if (step === Step.Search) {
      dataLayerViewedEvent({
        ...tagManagerEvent.FreeAssessment.SPOTIFY_SEARCH_NEW_ASSESSMENT,
        version: "V3",
      });
      return;
    }

    if (step === Step.Help) {
      dataLayerViewedEvent({
        ...tagManagerEvent.FreeAssessment.SPOTIFY_LINK_NEW_ASSESSMENT,
        version: "V3",
      });
      return;
    }
  }, [step]);

  const Footer = () => {
    return (
      <div className="footer">
        <Button
          className="botao-roxo"
          onClick={onClickContinue}
          disabled={!value}
        >
          {t("quiz.pages.FreeAssessment.continue")}
        </Button>
      </div>
    );
  };

  if (step === Step.Help) {
    return (
      <>
        <div className="full-item">
          <div className="caixa-input">
            <div className="field-tip">
              <h3>{t("quiz.pages.FreeAssessment.linkTip")}</h3>
              <div className="info">
                <div className="help-box">
                  <p>{t("quiz.pages.FreeAssessment.spotifyLinkTip")}</p>
                </div>

                <div className="spotify-card">
                  <img src={SpofityMockupLink} alt="Henrique & Juliano" />
                </div>
              </div>
            </div>
            <h1 style={{ marginTop: 80 }}>
              {t("quiz.pages.FreeAssessment.useLink")}
            </h1>
            <Input
              placeholder={t("quiz.pages.Questionario.searchPlaceholder")}
              onChange={(event) => setFieldValue(event.target.value)}
              onKeyPress={onKeyPress}
              style={{ marginTop: 14 }}
              value={fieldValue}
              suffix={
                <Button
                  className="botao-roxo"
                  style={{ padding: 0, borderRadius: 2 }}
                  onClick={onSearch}
                  htmlType="button"
                  loading={loading}
                >
                  {t("quiz.pages.Questionario.buscar")}
                </Button>
              }
            />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (step === Step.Select)
    return (
      <>
        <h1>
          {t("quiz.pages.FreeAssessment.results", {
            artistsCount: results.length,
          })}
        </h1>
        <div className="search-value">
          <span>{searchField}</span>
          <EditOutlined onClick={onClickEdit} />
        </div>
        <div className="full-item results">
          {results.map((result) => (
            <div
              key={result.id}
              onClick={() => onSelectArtist(result)}
              className="result-item"
            >
              <img
                src={result.avatarUrl ?? defaultArtistImage}
                alt={result.name}
                className="image"
                onError={(e) => (e.currentTarget.src = defaultArtistImage)}
              />
              <span className="name">{result.name}</span>
              <Radio checked={result.id === value?.id}></Radio>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
            <span className="help-tip">
              {t("quiz.pages.FreeAssessment.didNotFoundProfile")}{" "}
              <b
                onClick={() => {
                  setFieldValue("");
                  setStep(Step.Help);
                }}
              >
                {t("quiz.pages.FreeAssessment.clickHere")}
              </b>
            </span>
          </div>
        </div>
        <Footer />
      </>
    );

  return (
    <>
      <FontAwesomeIcon
        icon={faSpotify}
        className="label-icon"
        style={{ height: 32, color: "#ececec" }}
      />
      <h1>{t("quiz.pages.FreeAssessment.spotifyLabel")}</h1>
      {value?.avatarUrl && (
        <div className="profile-image">
          <img src={value?.avatarUrl} className="image" alt="artist" />
        </div>
      )}
      <span>{value?.name}</span>
      <div className="full-item">
        <div className="caixa-input">
          <Input
            placeholder={t("quiz.pages.Questionario.searchPlaceholder")}
            onChange={(event) => setFieldValue(event.target.value)}
            onKeyPress={onKeyPress}
            style={{ marginTop: 14 }}
            value={fieldValue}
            suffix={
              <Button
                className="botao-roxo"
                style={{ padding: 0, borderRadius: 2 }}
                onClick={onSearch}
                htmlType="button"
                loading={loading}
              >
                {t("quiz.pages.Questionario.buscar")}
              </Button>
            }
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 6,
            }}
          >
            <span className="help-tip">
              {t("quiz.pages.FreeAssessment.difficultyHint")},{" "}
              <b onClick={() => setStep(Step.Help)}>
                {t("quiz.pages.FreeAssessment.clickHere")}
              </b>
            </span>
          </div>

          <div className="field-tip">
            <h3>{t("quiz.pages.FreeAssessment.havingProblem")}</h3>
            <div className="info">
              <div className="help-box">
                <p>{t("quiz.pages.FreeAssessment.spotifyTip")}</p>
              </div>

              <div className="spotify-card">
                <img src={SpofityMockup} alt="Henrique & Juliano" />
              </div>
            </div>
          </div>
        </div>
        {/* <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 6,
          }}
        >
          <Checkbox
            onChange={({ target: { checked } }) => onClickNoValue(checked)}
            checked={value?.id === "no-data"}
          >
            <span>{t("quiz.pages.Questionario.noSpotify")}</span>
          </Checkbox>
        </div> */}
      </div>
      <Footer />
    </>
  );
};

export default SpotifySearch;
